import { z } from 'zod';

/**
 * Bits per solution: with `SOLUTION_FILTER_HASHES` hashes this puts the false-positive rate
 * near one in a million, so a client treating a hit as accepted is wrong about as often as
 * a dropped packet. A few hundred solutions cost about a kilobyte.
 */
const SOLUTION_FILTER_BITS_PER_WORD = 29;
const SOLUTION_FILTER_HASHES = 20;
/** Smallest filter, so a trivial board still hashes into distinct positions. */
const MIN_FILTER_BITS = 64;
/** The solver caps boards at 50,000 solutions; base64 of that filter is ~240 KB. */
const MAX_FILTER_CHARS = 262_144;

export const solutionFilterSchema = z.object({
  /** Base64 bit array, little-endian within each byte. */
  bits: z.string().max(MAX_FILTER_CHARS),
  k: z.number().int().min(1).max(64),
  seed: z.number().int().min(0).max(0xffffffff),
});
export type SolutionFilter = z.infer<typeof solutionFilterSchema>;

/** A filter decoded once for repeated membership tests. */
export interface DecodedSolutionFilter {
  bytes: Uint8Array;
  k: number;
  seed: number;
}

/** Build the round's filter; `seedSource` (the round id) makes each round's bit layout distinct. */
export function buildSolutionFilter(solutions: readonly string[], seedSource: string): SolutionFilter {
  const seed = murmur3(seedSource, 0);
  const bitCount = Math.max(
    MIN_FILTER_BITS,
    Math.ceil((solutions.length * SOLUTION_FILTER_BITS_PER_WORD) / 8) * 8,
  );
  const bytes = new Uint8Array(bitCount / 8);
  for (const word of solutions) {
    for (const index of positions(word, seed, SOLUTION_FILTER_HASHES, bitCount))
      bytes[index >>> 3]! |= 1 << (index & 7);
  }
  return { bits: encodeBase64(bytes), k: SOLUTION_FILTER_HASHES, seed };
}

export function decodeSolutionFilter(filter: SolutionFilter): DecodedSolutionFilter {
  return { bytes: decodeBase64(filter.bits), k: filter.k, seed: filter.seed };
}

/** False means the word is certainly not a solution; true means it almost certainly is. */
export function mightBeSolution(filter: DecodedSolutionFilter, word: string): boolean {
  const bitCount = filter.bytes.length * 8;
  if (bitCount === 0) return false;
  for (const index of positions(word, filter.seed, filter.k, bitCount)) {
    if ((filter.bytes[index >>> 3]! & (1 << (index & 7))) === 0) return false;
  }
  return true;
}

/** Kirsch–Mitzenmacher double hashing: k positions from two 32-bit hashes. */
function* positions(word: string, seed: number, k: number, bitCount: number): Generator<number> {
  const h1 = murmur3(word, seed);
  const h2 = murmur3(word, ~seed >>> 0) | 1;
  for (let i = 0; i < k; i++) yield ((h1 + i * h2) >>> 0) % bitCount;
}

/** MurmurHash3 x86 32-bit over the word's UTF-16 code units; words are lowercase ASCII. */
function murmur3(text: string, seed: number): number {
  let h = seed >>> 0;
  const c1 = 0xcc9e2d51;
  const c2 = 0x1b873593;
  for (let i = 0; i < text.length; i++) {
    let k = text.charCodeAt(i);
    k = Math.imul(k, c1);
    k = (k << 15) | (k >>> 17);
    k = Math.imul(k, c2);
    h ^= k;
    h = (h << 13) | (h >>> 19);
    h = (Math.imul(h, 5) + 0xe6546b64) >>> 0;
  }
  h ^= text.length;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
